<script>
  import { onMount } from 'svelte';
  import { currentSection, isAuthenticated } from './stores/adminStore';
  import { checkSession } from './services/api';
  import Sidebar from './components/Sidebar.svelte';
  import Dashboard from './pages/Dashboard.svelte';
  import Apps from './pages/Apps.svelte';
  import Resources from './pages/Resources.svelte';
  import Settings from './pages/Settings.svelte';
  import Logs from './pages/Logs.svelte';

  let loading = true;

  onMount(async () => {
    const session = await checkSession();
    isAuthenticated.set(session.valid);
    loading = false;

    if (!session.valid) {
      // Redirect to landing page for login
      window.location.href = 'https://msalgoai.com';
    }
  });

  function getCurrentTime() {
    return new Date().toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  }

  let time = getCurrentTime();
  setInterval(() => { time = getCurrentTime(); }, 1000);
</script>

{#if loading}
  <div class="loading-screen">
    <div class="loading-logo">
      <div class="logo-circle">
        <span class="logo-ms">MS</span>
        <span class="logo-trade">TRADE</span>
      </div>
    </div>
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading Admin Panel...</span>
  </div>
{:else if $isAuthenticated}
  <div class="admin-layout">
    <Sidebar />

    <main class="main-content">
      <header class="top-header">
        <div class="header-breadcrumb">
          <span class="breadcrumb-platform">MS-TRADE</span>
          <span class="breadcrumb-sep">›</span>
          <span class="breadcrumb-section">{$currentSection.charAt(0).toUpperCase() + $currentSection.slice(1)}</span>
        </div>
        <div class="header-right">
          <span class="header-time">{time} IST</span>
          <div class="header-user">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="8" r="4"/><path d="M4 21v-1a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v1"/>
            </svg>
            <span>Admin</span>
          </div>
        </div>
      </header>

      <div class="content-area">
        {#if $currentSection === 'dashboard'}
          <Dashboard />
        {:else if $currentSection === 'apps'}
          <Apps />
        {:else if $currentSection === 'resources'}
          <Resources />
        {:else if $currentSection === 'settings'}
          <Settings />
        {:else if $currentSection === 'logs'}
          <Logs />
        {/if}
      </div>

      <footer class="bottom-footer">
        <span class="footer-brand">MS-TRADE Admin Panel v1.0</span>
        <span class="footer-time">Last updated: {time} IST</span>
      </footer>
    </main>
  </div>
{/if}

<style>
  /* Loading Screen */
  .loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    gap: 24px;
  }

  .loading-logo .logo-circle {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 2px solid var(--neon-blue);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 24px rgba(59, 130, 246, 0.3);
    animation: glowPulse 2s infinite;
  }

  .loading-logo .logo-ms {
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    color: var(--text-primary);
  }

  .loading-logo .logo-trade {
    font-size: 8px;
    font-weight: 600;
    color: var(--text-secondary);
    letter-spacing: 0.15em;
    line-height: 1;
    margin-top: 2px;
  }

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--border-default);
    border-top-color: var(--neon-blue);
    border-radius: 50%;
    animation: spinSlow 0.8s linear infinite;
  }

  .loading-text {
    font-size: 14px;
    color: var(--text-secondary);
  }

  /* Layout */
  .admin-layout {
    display: flex;
    min-height: 100vh;
  }

  .main-content {
    flex: 1;
    margin-left: var(--sidebar-width);
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  /* Header */
  .top-header {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 32px;
    height: var(--header-height);
    background: rgba(10, 14, 21, 0.92);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border-default);
  }

  .header-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }

  .breadcrumb-platform {
    color: var(--text-secondary);
    font-weight: 500;
  }

  .breadcrumb-sep {
    color: var(--text-tertiary);
  }

  .breadcrumb-section {
    color: var(--text-primary);
    font-weight: 600;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .header-time {
    font-size: 13px;
    color: var(--text-secondary);
    font-variant-numeric: tabular-nums;
  }

  .header-user {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-secondary);
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm);
  }

  /* Content */
  .content-area {
    flex: 1;
    padding: 28px 32px;
    max-width: 1200px;
    width: 100%;
  }

  /* Footer */
  .bottom-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 32px;
    border-top: 1px solid var(--border-default);
    font-size: 12px;
    color: var(--text-tertiary);
  }

  .footer-brand {
    font-weight: 500;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .main-content {
      margin-left: 0;
      margin-bottom: 60px; /* Space for mobile bottom nav */
    }

    .top-header {
      padding: 0 16px;
    }

    .content-area {
      padding: 20px 16px;
    }

    .header-time { display: none; }

    .bottom-footer {
      padding: 10px 16px;
      flex-direction: column;
      gap: 4px;
    }
  }

  @media (min-width: 769px) and (max-width: 1199px) {
    .content-area {
      padding: 24px;
    }
  }
</style>
